import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import { truncateStringInTheMiddle } from '@swarm/core/shared/utils'
import {
  formatBigInt,
  prettifyBalance,
} from '@swarm/core/shared/utils/formatting'
import { normalize } from '@swarm/core/shared/utils/helpers/big-helpers'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import ExplorerLink from '@swarm/ui/swarm/ExplorerLink'
import TokenIcon from '@swarm/ui/swarm/TokenIcon'
import { BigSource } from 'big.js'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Flex, Text } from 'rimble-ui'
import styled from 'styled-components/macro'
import { layout, LayoutProps } from 'styled-system'

import { useStakingCreationContext } from '../StakeSecurityContext'
import { calcAPY, calcStakedSecuritySMT } from '../utils'

const Td = styled.td<LayoutProps>`
  ${layout}
`

const StyledName = styled(Text)`
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
`

interface SecurityRowProps {
  security: ExtendedSecurity
  smtReward?: BigSource
}

const RwaRow = ({ security, smtReward }: SecurityRowProps) => {
  const {
    id: securityId,
    name,
    symbol,
    smtStaked,
    marketCap,
    volume24hs,
  } = security

  const { nativeSmt } = useNativeSmt()
  const { t } = useTranslation('staking')
  const { openStakeSecurityPopup } = useStakingCreationContext()

  const formattedMarketCap = !marketCap ? '--' : `$${formatBigInt(marketCap)}`

  const tokenVolume24hs = !volume24hs ? '--' : `$${formatBigInt(volume24hs)}`

  const myTotalSecurityStaked = !nativeSmt
    ? '--'
    : prettifyBalance(
        normalize(calcStakedSecuritySMT(security), nativeSmt.decimals),
      )

  const formattedApy = useMemo(() => {
    if (!smtReward || !nativeSmt || !security.smtStaked) return 0
    const apy = calcAPY(
      normalize(smtReward, nativeSmt.decimals),
      security.smtStaked,
    ).toFixed(2)
    return `${apy}%`
  }, [security, nativeSmt, smtReward])

  const formattedSmtStaked = smtStaked ? formatBigInt(smtStaked) : '--'

  return (
    <tr>
      <Td title={securityId}>
        <ExplorerLink
          type="token"
          hash={securityId}
          label={truncateStringInTheMiddle(securityId)}
        />
      </Td>
      <Td title={`${name} (${symbol})`}>
        <Flex>
          <TokenIcon
            width="25px"
            height="25px"
            symbol={symbol}
            name={name}
            maxWidth="none"
          />
          <StyledName ml="12px" color="primary" fontWeight="bold">
            {name}
          </StyledName>
          <Text color="primary" fontWeight="bold" ml={1}>
            ({symbol})
          </Text>
        </Flex>
      </Td>
      <Td style={{ textAlign: 'center' }}>{formattedMarketCap}</Td>
      <Td style={{ textAlign: 'center' }}>{tokenVolume24hs}</Td>
      <Td style={{ textAlign: 'center' }}>{formattedSmtStaked}</Td>
      <Td style={{ textAlign: 'center' }}>{formattedApy}</Td>
      <Td style={{ textAlign: 'center' }}>{myTotalSecurityStaked}</Td>
      <Td>
        <Button
          onClick={() => openStakeSecurityPopup(security)}
          lineHeight="20px"
          height="40px"
          width="100px"
          fontSize="14px"
          mr="16px"
          px={['4px', '16px']}
        >
          {t('actions.stake')}
        </Button>
      </Td>
    </tr>
  )
}

export default RwaRow
